import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useCallback, useEffect } from 'react';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { useChannelsStore } from '@/components/pages/channels/use-channels-store';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Title } from '@/components/ui/title';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { useOpeningContext } from '@/contexts/opening-context';
import { useCampaignRefetch } from '@/hooks/use-campaign-refetch';
import { useHireUrl } from '@/hooks/use-hire-url';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';
import { formatShortDate } from '@/utils/date-util';

import { AutomationStatusPill } from './automation-status-pill';
import { HiringGoal } from './hiring-goal';
import { Tabs } from './tabs';
import { useEditTargets } from './use-edit-targets';

function OverviewContent() {
  const openingSlug = useOpeningSlug();
  const setOpening = useChannelsStore(s => s.setOpening);
  const {
    setAnalyticsData,
    setHiringGoal,
    setOpeningTitle,
    setHiringGoalUrl,
    currentHiringGoalId,
    setHasActiveHiringGoal,
    showAggregateHires,
    showAggregateSpend,
  } = useOpeningContext();
  const { setRefetchFn: setEditTargetsRefetchFn } = useEditTargetsContext();
  const { getHireATHiringGoalUrl } = useHireUrl();

  const { data: funnelData, refetch } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.detail,
      openingSlug,
      currentHiringGoalId,
      showAggregateHires,
      showAggregateSpend,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpenings(
        openingSlug,
        currentHiringGoalId,
        showAggregateHires,
        showAggregateSpend,
      ),
  });

  const refetchFn = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useCampaignRefetch(refetchFn);

  useEffect(() => {
    setOpening({ id: funnelData.opening_id, title: funnelData.title });
    setAnalyticsData(funnelData.analytics);
    setEditTargetsRefetchFn(refetchFn);
    setOpeningTitle(funnelData.title);
    setHiringGoalUrl(
      getHireATHiringGoalUrl(
        funnelData.brand_slug ?? 'no_brand',
        funnelData.location_id,
        funnelData.opening_id,
      ),
    );
    setHasActiveHiringGoal(funnelData.has_active_hiring_goal);
  }, [
    funnelData,
    setOpening,
    setAnalyticsData,
    setEditTargetsRefetchFn,
    setOpeningTitle,
    setHiringGoalUrl,
    getHireATHiringGoalUrl,
    refetchFn,
    setHasActiveHiringGoal,
  ]);

  useEditTargets(funnelData);

  const {
    active,
    title,
    sourcing_status: sourcingStatus,
    hiring_goal: hiringGoal,
    automation,
    has_active_hiring_goal: hasActiveHiringGoal,
  } = funnelData;

  const performanceStatusMetrics = hiringGoal?.performance_status_metrics;
  const { status, criteria } = performanceStatusMetrics ?? {};

  useEffect(() => {
    if (hiringGoal) {
      setHiringGoal(hiringGoal);
    }
  }, [hiringGoal, setHiringGoal]);

  return (
    <div className="shadow-base flex flex-col gap-4 rounded border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex flex-col items-start gap-6 py-1.5 lg:flex-row lg:justify-between lg:gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <OpeningStatus active={active} />
            <Title title={title} />
          </div>

          <div className="flex items-center gap-2">
            <SourcingStatusPill status={sourcingStatus} />
            {status && (
              <PerformanceStatusPill
                status={status}
                criteria={criteria ?? null}
                expected={
                  (criteria === 'hire'
                    ? performanceStatusMetrics?.expected_hires_count
                    : performanceStatusMetrics?.expected_applicants_count) ??
                  null
                }
                actual={
                  (criteria === 'hire'
                    ? funnelData.analytics.hire_stats.actual
                    : funnelData.analytics.applicants_stats.actual) ?? null
                }
                startDate={formatShortDate(
                  funnelData.hiring_goal?.start_date ?? '',
                )}
                endDate={formatShortDate(funnelData.hiring_goal?.hire_by ?? '')}
                duration={funnelData.hiring_goal?.duration}
              />
            )}
            {hasActiveHiringGoal && (
              <AutomationStatusPill automation={automation} />
            )}
          </div>
        </div>

        <HiringGoal
          hiringGoal={hiringGoal}
          hireATHiringGoalUrl={getHireATHiringGoalUrl(
            funnelData.brand_slug ?? 'no_brand',
            funnelData.location_id,
            funnelData.opening_id,
          )}
        />
      </div>
      <Tabs />
    </div>
  );
}

export function Overview() {
  return (
    <Suspense fallback={<Overview.Skeleton />}>
      <OverviewContent />
    </Suspense>
  );
}

Overview.Skeleton = function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-4 border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          <Title.Skeleton />
          <SourcingStatusPill.Skeleton />
          <PerformanceStatusPill.Skeleton />
        </div>
        <HiringGoal.Skeleton />
      </div>
      <Tabs />
    </div>
  );
};
